import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State } from "@stencil/core";
import { checkFileType } from "./utils/checkFileType";

let fileInputIdCounter = 0;

@Component({
    tag: "ux-file-upload",
    styleUrl: "file-upload.scss",
    shadow: false
})
export class FileUpload {
    @Element() element!: HTMLUxFileUploadElement;
    private baseClass = "ux-file-upload";
    private fileUploadField: HTMLInputElement | null = null;
    private fileContainer: HTMLDivElement | null = null;
    private fileInputIdentifier = "data-file-input-id";

    /**
     * Option to send multiple files in one request
     */
    @Prop() readonly maxFiles: number = 3;

    /**
     * The maximum upload file size in MB
     */
    @Prop() readonly maxFileSize: number = 20;

    /**
     * Accepted file types in comma separated list of mime types or file extensions
     */
    @Prop() readonly acceptedFiles: string = "image/*,application/pdf";

    /**
     * Message Upload limit reached
     */
    @Prop() readonly messageMaxFilesReached: string = "Upload limit reached";

    /**
     * Message Upload limit reached
     */
    @Prop() readonly messageMaxFileSize: string = "One of the uploaded files was too big";

    /**
     * Message invalid file extension
     */
    @Prop() readonly messageInvalidFile: string = "One of the uploaded files did not have the right file extension";

    /**
     * Generic error message to be displayed
     */
    @Prop({ mutable: true }) errorMsg = "";

    /**
     * Label text describing the file upload field
     */
    @Prop() readonly label: string = "";

    /**
     * Indicates if the file upload is disabled
     */
    @Prop() readonly disabled: boolean = false;

    /**
     * Indicates if the file upload is required
     */
    @Prop() readonly required: boolean = false;

    /**
     * Indicates if the file upload has an error state. Use error attribute only for static error rendering.
     */
    @Prop({ mutable: true }) error = false;

    /**
     * Name of the input field
     */
    @Prop() readonly name: string = "";

    /**
     * Indicates if component has bulk delete functionality
     */
    @Prop() readonly bulkDelete: boolean = false;

    private effectiveAllowedSize: number;
    private allowedFileTypes: string[];

    @State() message = "";
    @State() uploadedFilesCount = 0;

    /**
     * Emitted when files are added or removed
     */
    @Event({
        eventName: "uxFileChange",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    private uxFileChange!: EventEmitter<{
        files: File[];
    }>;

    componentWillRender() {
        this.effectiveAllowedSize = this.maxFileSize * 1024 * 1024;
        this.allowedFileTypes = this.acceptedFiles.split(",");
    }

    connectedCallback() {
        this.element.addEventListener("change", this.onInputChange);
        this.element.addEventListener("dragenter", this.onDragEnter);
        this.element.addEventListener("dragleave", this.onDragLeave);
        this.element.addEventListener("dragover", this.stopEvent);
        this.element.addEventListener("drop", this.handleDrop);
    }

    private onDragLeave = (e: Event) => {
        this.stopEvent(e);
    };

    private onDragEnter = (e: Event) => {
        this.stopEvent(e);
    };

    private stopEvent = (e: Event) => {
        e.stopPropagation();
        e.preventDefault();
    };

    private handleDrop = (e: DragEvent) => {
        this.stopEvent(e);
        const dt = e.dataTransfer;
        const files = dt?.files || new FileList();
        this.checkAndAddFiles(files);
    };

    private collectFiles = (): File[] => {
        const filesArray: File[] = [];
        if (!this.fileContainer) {
            return filesArray;
        }
        this.fileContainer.querySelectorAll<HTMLInputElement>(`input[type="file"]`).forEach((input) => {
            if (input.files) {
                for (const file of input.files) {
                    filesArray.push(file);
                }
            }
        });
        return filesArray;
    };

    private emitChangeEvent = () => {
        this.uxFileChange.emit({ files: this.collectFiles() });
    };

    private onInputChange = () => {
        const files = this.fileUploadField?.files || new FileList();
        this.checkAndAddFiles(files);
        this.emitChangeEvent();
    };

    private resetFileUploadField = () => {
        if (this.fileUploadField) {
            this.fileUploadField.files = null;
            this.fileUploadField.value = "";
        }
    };

    private checkAndAddFiles = (fileList: FileList) => {
        this.checkAndAddIndividualFiles(fileList);
        this.resetFileUploadField();
        this.updateUploadedFilesCount();
    };

    private checkAndAddIndividualFiles = (fileList: FileList) => {
        if (!this.fileContainer) {
            return;
        }
        for (const file of fileList) {
            if (this.maxFiles === this.uploadedFilesCount) {
                this.error = true;
                this.errorMsg = this.messageMaxFilesReached;
                throw new Error(`UxFileUpload: Too many files uploaded. Max files allowed is ${this.maxFiles}`);
            } else if (file.size > this.effectiveAllowedSize) {
                this.error = true;
                this.errorMsg = this.messageMaxFileSize;
                throw new Error("UxFileUpload: File size too big");
            } else if (!checkFileType(file, this.allowedFileTypes)) {
                this.error = true;
                this.errorMsg = this.messageInvalidFile;
                throw new Error("UxFileUpload: Invalid file extension");
            } else {
                const dataTransfer = new DataTransfer();
                dataTransfer.items.add(file);
                const newFileIput = document.createElement("input");
                newFileIput.setAttribute("type", "file");
                newFileIput.setAttribute(this.fileInputIdentifier, String(fileInputIdCounter++));
                newFileIput.setAttribute("name", this.name);
                newFileIput.files = dataTransfer.files;
                this.fileContainer.insertAdjacentElement("beforeend", newFileIput);
                this.updateUploadedFilesCount();
            }
        }
    };

    private updateUploadedFilesCount = () => {
        let totalFiles = 0;
        const fileInputs = this.fileContainer?.querySelectorAll("input") || [];
        if (fileInputs.length === 0) {
            this.uploadedFilesCount = 0;
        } else {
            fileInputs.forEach((file) => {
                if (file.files) {
                    totalFiles += file.files.length;
                }
            });
        }
        this.uploadedFilesCount = totalFiles;
    };

    private renderThumbnails = (): HTMLElement[] | null => {
        if (this.fileContainer === null) {
            return null;
        }
        const thumbnails: HTMLElement[] = [];
        const inputs = this.fileContainer.querySelectorAll("input");
        if (inputs.length === 0) {
            return null;
        }
        for (const input of inputs) {
            const files = input.files || new FileList();
            for (const file of files) {
                thumbnails.push(
                    <div
                        class={{
                            [`${this.baseClass}__thumbnail-container`]: true
                        }}
                    >
                        <ux-file-upload-thumbnail
                            key={file.name}
                            file-type={file.type}
                            file={file}
                            is-individual={true}
                            file-input-id={input.getAttribute(this.fileInputIdentifier)}
                            file-name={file.name}
                        />
                    </div>
                );
            }
        }
        if (thumbnails.length === 0) {
            return null;
        }
        return thumbnails;
    };

    @Listen("uxFileUploadDelete")
    deleteInput(e: CustomEvent) {
        if (this.fileContainer) {
            const inputToDelete = this.fileContainer.querySelector<HTMLInputElement>(
                `input[${this.fileInputIdentifier}="${e.detail.fileInputId}"]`
            );
            if (inputToDelete === null) {
                return;
            }
            inputToDelete.remove();
        } else {
            if (this.fileUploadField) {
                this.fileUploadField.value = "";
            }
        }
        const filesCount = this.uploadedFilesCount;
        this.updateUploadedFilesCount();
        if (this.uploadedFilesCount !== filesCount) {
            this.emitChangeEvent();
        }
    }

    @Listen("focusout", { target: "window" })
    resetErrorOnBlur(e: Event) {
        const eTarget = e.target as HTMLElement;
        if (eTarget.closest("ux-file-upload") !== this.element) {
            return;
        }
        this.error = false;
    }

    /** Remove all files and reset inputs */
    @Method()
    async reset() {
        this.deleteAllFiles();
    }

    private deleteAllFiles = (e?: Event) => {
        if (e) {
            e.preventDefault();
        }
        const filesCount = this.uploadedFilesCount;
        this.resetFileUploadField();
        if (this.fileContainer) {
            const fileInputs = this.fileContainer.querySelectorAll("input");
            if (fileInputs.length === 0) {
                return;
            }
            fileInputs.forEach((fileInput) => fileInput.remove());
        }
        this.updateUploadedFilesCount();
        if (this.uploadedFilesCount !== filesCount) {
            this.emitChangeEvent();
        }
    };

    private checkItemsPerRow = () => {
        return getComputedStyle(this.element).getPropertyValue("--ux-file-upload-items-per-row");
    };

    render() {
        const { label, disabled, required, error, errorMsg } = this;

        return (
            <Host
                class={{
                    [`${this.baseClass}`]: true,
                    [`${this.baseClass}--disabled`]: disabled,
                    [`${this.baseClass}--error`]: error
                }}
            >
                {(label !== undefined || (error && errorMsg !== "")) && (
                    <div class="ux-label-container">
                        {label !== "" && (
                            <label class="ux-label">
                                {label}
                                {required && <span> *</span>}
                            </label>
                        )}{" "}
                        {error && errorMsg !== "" && <span class={`ux-label ux-label--message ux-label--error`}>{errorMsg}</span>}
                    </div>
                )}
                <div class={`${this.baseClass}__file-container`} ref={(el) => (this.fileContainer = el || null)} />
                <div
                    class={{
                        [`${this.baseClass}__container`]: true,
                        [`${this.baseClass}__container--bulk-delete`]: this.bulkDelete,
                        [`${this.baseClass}__container--items-per-row`]: this.checkItemsPerRow() !== "auto"
                    }}
                >
                    {h(this.renderThumbnails)}
                    {this.uploadedFilesCount < this.maxFiles && (
                        <div
                            class={{
                                [`${this.baseClass}__action-container`]: true
                            }}
                        >
                            <div
                                class={{
                                    [`${this.baseClass}__action-container-inner`]: true
                                }}
                            >
                                <div
                                    class={{
                                        [`${this.baseClass}__add-file`]: true,
                                        [`${this.baseClass}__add-file--with-thumbnails`]: this.uploadedFilesCount !== 0
                                    }}
                                >
                                    <ux-icon name="cross" weight="normal" />
                                </div>
                                {this.uploadedFilesCount !== 0 && this.bulkDelete && (
                                    <button class={`${this.baseClass}__delete-all`} onClick={this.deleteAllFiles}>
                                        <ux-icon name="bin" weight="normal" />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    <label class={`${this.baseClass}__label`}>
                        <input
                            type="file"
                            multiple={this.maxFiles > 1 ? true : undefined}
                            class={`${this.baseClass}__input-field`}
                            ref={(el) => (this.fileUploadField = el || null)}
                            onFocus={() => (this.error = false)}
                            required={this.required && !this.uploadedFilesCount}
                            aria-required={this.required}
                            aria-invalid={error ? "true" : "false"}
                            accept={this.acceptedFiles}
                        />
                    </label>
                </div>
            </Host>
        );
    }
}
